import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable from "components/DataTable/DataTable.jsx";

import api from "api/index";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" },
  sectionTitle: { color: "#1a237e", fontWeight: "bold", fontSize: "14px", margin: 0 },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: { width: "100%", marginTop: "8px" },
  emptyState: { textAlign: "center", color: "#999", padding: "20px" }
};

class DepartmentProfile extends Component {
  state = {
    department: null,
    loading: true,
    error: null,
    allProgrammes: [],
    allStaff: [],
    attachProgrammeOpen: false,
    attachStaffOpen: false,
    pickedProgrammeId: "",
    pickedStaffId: "",
    saving: false,
    dialogError: null
  };

  componentDidMount() {
    this.fetchDepartment();
    this.fetchAllProgrammes();
    this.fetchAllStaff();
  }

  fetchDepartment = async () => {
    const { id } = this.props.match.params;
    this.setState({ loading: true });
    try {
      const res = await api.get(`/departments/${id}`);
      this.setState({ department: res.data.data || res.data, loading: false });
    } catch (e) {
      this.setState({ error: "Department not found.", loading: false });
    }
  };

  fetchAllProgrammes = async () => {
    try {
      const res = await api.get("/programmes", { params: { limit: 500 } });
      const d = res.data.data || res.data;
      this.setState({ allProgrammes: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchAllStaff = async () => {
    try {
      const res = await api.get("/staff", { params: { limit: 500 } });
      const d = res.data.data || res.data;
      this.setState({ allStaff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  refetchAll = () => {
    this.fetchDepartment();
    this.fetchAllProgrammes();
    this.fetchAllStaff();
  };

  attachProgramme = async () => {
    const { department, pickedProgrammeId } = this.state;
    if (!pickedProgrammeId) { this.setState({ dialogError: "Select a programme first." }); return; }
    this.setState({ saving: true, dialogError: null });
    try {
      await api.put(`/programmes/${pickedProgrammeId}`, { departmentId: department.id });
      this.setState({ attachProgrammeOpen: false, saving: false, pickedProgrammeId: "" });
      this.refetchAll();
    } catch (e) {
      this.setState({ dialogError: e.response?.data?.message || "Failed to attach programme.", saving: false });
    }
  };

  removeProgramme = async programmeId => {
    try {
      await api.put(`/programmes/${programmeId}`, { departmentId: "" });
      this.refetchAll();
    } catch (e) { /* silent */ }
  };

  attachStaff = async () => {
    const { department, pickedStaffId } = this.state;
    if (!pickedStaffId) { this.setState({ dialogError: "Select a staff member first." }); return; }
    this.setState({ saving: true, dialogError: null });
    try {
      await api.put(`/staff/${pickedStaffId}`, { departmentId: department.id });
      this.setState({ attachStaffOpen: false, saving: false, pickedStaffId: "" });
      this.refetchAll();
    } catch (e) {
      this.setState({ dialogError: e.response?.data?.message || "Failed to attach staff.", saving: false });
    }
  };

  removeStaff = async staffId => {
    try {
      await api.put(`/staff/${staffId}`, { departmentId: "" });
      this.refetchAll();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes, history } = this.props;
    const {
      department, loading, error, allProgrammes, allStaff,
      attachProgrammeOpen, attachStaffOpen, pickedProgrammeId, pickedStaffId,
      saving, dialogError
    } = this.state;

    if (loading) return <div style={{ textAlign: "center", padding: "60px" }}><CircularProgress /></div>;
    if (error) return <p style={{ color: "#f44336", padding: "20px" }}>{error}</p>;

    const programmes = department.programmes || [];
    const staff = department.staff || [];
    const attachableProgrammes = allProgrammes.filter(p => p.departmentId !== department.id);
    const attachableStaff = allStaff.filter(s => s.departmentId !== department.id);

    const programmeColumns = [
      { id: "code", header: "Code", accessorFn: p => p.code || "—", meta: { variant: "mono" } },
      { id: "name", header: "Programme Name", accessorFn: p => p.name },
      { id: "level", header: "Level", accessorFn: p => p.level || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="danger" simple onClick={() => this.removeProgramme(info.row.original.id)}>Remove</Button>
        )
      }
    ];

    const staffColumns = [
      { id: "staffNo", header: "Staff No", accessorFn: s => s.staffNo || "—", meta: { variant: "mono" } },
      { id: "name", header: "Name", accessorFn: s => `${s.firstName || ""} ${s.lastName || ""}`.trim() },
      { id: "role", header: "Role", accessorFn: s => s.role || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="danger" simple onClick={() => this.removeStaff(info.row.original.id)}>Remove</Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>{department.name}</h4>
                <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                  {programmes.length} programme{programmes.length !== 1 ? "s" : ""} &middot; {staff.length} staff
                </p>
              </div>
              <Button size="sm" color="white" onClick={() => history.push("/bwillis/academic/departments")}>Back</Button>
            </CardHeader>
            <CardBody>
              <div style={{ marginTop: "8px" }}>
                <DataTable
                  title="Attached Programmes"
                  actions={(
                    <Button size="sm" color="primary" onClick={() => this.setState({ attachProgrammeOpen: true, pickedProgrammeId: "", dialogError: null })}>
                      <Icon style={{ marginRight: "4px", fontSize: "16px" }}>add</Icon> Attach Programme
                    </Button>
                  )}
                  columns={programmeColumns}
                  data={programmes}
                  emptyState="No programmes attached to this department."
                />
              </div>

              <div style={{ marginTop: "32px" }}>
                <DataTable
                  title="Attached Staff"
                  actions={(
                    <Button size="sm" color="primary" onClick={() => this.setState({ attachStaffOpen: true, pickedStaffId: "", dialogError: null })}>
                      <Icon style={{ marginRight: "4px", fontSize: "16px" }}>add</Icon> Attach Staff
                    </Button>
                  )}
                  columns={staffColumns}
                  data={staff}
                  emptyState="No staff attached to this department."
                />
              </div>
            </CardBody>
          </Card>
        </GridItem>

        {/* Attach Programme Dialog */}
        <Dialog open={attachProgrammeOpen} onClose={() => this.setState({ attachProgrammeOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader title="Attach Programme" />
          <DialogContent>
            {dialogError && <p className={classes.errorMsg}>{dialogError}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Programme</InputLabel>
              <Select value={pickedProgrammeId} onChange={e => this.setState({ pickedProgrammeId: e.target.value })}>
                <MenuItem value="">— Select Programme —</MenuItem>
                {attachableProgrammes.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ""}{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ attachProgrammeOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.attachProgramme} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Attach"}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Attach Staff Dialog */}
        <Dialog open={attachStaffOpen} onClose={() => this.setState({ attachStaffOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader title="Attach Staff" />
          <DialogContent>
            {dialogError && <p className={classes.errorMsg}>{dialogError}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Staff Member</InputLabel>
              <Select value={pickedStaffId} onChange={e => this.setState({ pickedStaffId: e.target.value })}>
                <MenuItem value="">— Select Staff —</MenuItem>
                {attachableStaff.map(s => (
                  <MenuItem key={s.id} value={s.id}>{`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.staffNo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ attachStaffOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.attachStaff} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Attach"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

DepartmentProfile.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired, match: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(DepartmentProfile));
